/*
 * Copyright 2026 Sony Corporation
 */
import { GATES, gateOfMetric, type RunSummary } from '@ips-jit/schema';

/**
 * How a run ended, in the dashboard's terms. `delivered` ran on the device; `built` passed verification but was not
 * sent (no device chosen, or the device did not answer); `answered` is a question answered or a cause found outside
 * the code; `open` is still going.
 */
export type Outcome = 'delivered' | 'built' | 'answered' | 'failed' | 'rejected' | 'superseded' | 'open';

export const outcomeOf = (run: RunSummary): Outcome => {
  switch (run.status) {
    case 'planning':
    case 'awaiting_approval':
    case 'implementing':
      return 'open';
    case 'rejected':
    case 'superseded':
    case 'failed':
      return run.status;
    case 'done':
      if (run.metrics.applied) {
        return 'delivered';
      }
      return run.version !== null ? 'built' : 'answered';
  }
};

/** The gates a run was turned back at, with how often, in loop order. */
export const failuresOf = (run: RunSummary): { gate: string; count: number }[] => {
  const failures = Object.entries(run.metrics)
    .map(([key, count]) => ({ gate: gateOfMetric(key), count }))
    .filter((f): f is { gate: string; count: number } => f.gate !== undefined && f.count > 0);
  const order = (gate: string) => {
    const index = (GATES as readonly string[]).indexOf(gate);
    return index === -1 ? GATES.length : index;
  };
  return failures.sort((a, b) => order(a.gate) - order(b.gate));
};

/** A run that went through building (as opposed to one that only planned or answered). */
export const wasBuilt = (run: RunSummary): boolean => run.metrics.implement_ms !== undefined;

/** Passed every gate the first time: one build, nothing turned back. */
export const isFirstPass = (run: RunSummary): boolean =>
  outcomeOf(run) === 'delivered' && run.metrics.builds === 1 && failuresOf(run).length === 0;

export const median = (values: number[]): number | undefined => {
  if (values.length === 0) {
    return undefined;
  }
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
};

export interface GateCount {
  gate: string;
  /** How many times the gate turned work back, over all runs. */
  count: number;
  /** How many runs it turned back at least once. */
  runs: number;
}

export interface Summary {
  /** Runs a person started, and runs the cloud started for a device error. */
  requests: number;
  autoFixes: number;
  outcomes: Record<Outcome, number>;
  /** Of the runs that went through building. */
  built: number;
  delivered: number;
  firstPass: number;
  /** Medians over the runs that went through building, in milliseconds and tokens. */
  medianTotalMs?: number;
  medianBuildMs?: number;
  medianBuilds?: number;
  medianTokensIn?: number;
  medianTokensOut?: number;
  gates: GateCount[];
  autoFix: {
    /** Fixes that ran on the device and stayed quiet while watched. */
    held: number;
    /** Fixes after which the error came back. */
    cameBack: number;
    /** Runs that found the cause outside the code and changed nothing. */
    notCode: number;
    failed: number;
  };
}

const EMPTY_OUTCOMES: Record<Outcome, number> = {
  delivered: 0,
  built: 0,
  answered: 0,
  failed: 0,
  rejected: 0,
  superseded: 0,
  open: 0,
};

export const summarize = (runs: RunSummary[]): Summary => {
  const outcomes = { ...EMPTY_OUTCOMES };
  const gates = new Map<string, GateCount>();
  const autoFix = { held: 0, cameBack: 0, notCode: 0, failed: 0 };
  const builtRuns = runs.filter(wasBuilt);

  for (const run of runs) {
    const outcome = outcomeOf(run);
    outcomes[outcome] += 1;
    for (const { gate, count } of failuresOf(run)) {
      const entry = gates.get(gate) ?? { gate, count: 0, runs: 0 };
      entry.count += count;
      entry.runs += 1;
      gates.set(gate, entry);
    }
    if (run.source === 'device_error') {
      if (outcome === 'delivered') {
        if (run.metrics.fail_runtime) {
          autoFix.cameBack += 1;
        } else {
          autoFix.held += 1;
        }
      } else if (outcome === 'answered') {
        autoFix.notCode += 1;
      } else if (outcome === 'failed') {
        autoFix.failed += 1;
      }
    }
  }

  const of = (key: string) =>
    builtRuns.map((run) => run.metrics[key]).filter((value): value is number => value !== undefined);
  const totals = builtRuns.map((run) => (run.metrics.plan_ms ?? 0) + (run.metrics.implement_ms ?? 0));

  return {
    requests: runs.filter((run) => run.source === 'user').length,
    autoFixes: runs.filter((run) => run.source === 'device_error').length,
    outcomes,
    built: builtRuns.length,
    delivered: outcomes.delivered,
    firstPass: runs.filter(isFirstPass).length,
    medianTotalMs: median(totals),
    medianBuildMs: median(of('build_ms')),
    medianBuilds: median(of('builds')),
    medianTokensIn: median(of('input_tokens')),
    medianTokensOut: median(of('output_tokens')),
    gates: [...gates.values()].sort((a, b) => b.count - a.count),
    autoFix,
  };
};
